import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { VIDEO_CATEGORIES } from "@demore/shared/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

type Item = {
  id: string;
  sentByUserId: string;
  recipientName: string;
  recipientCompany: string | null;
  createdAt: string;
  firstOpenedAt: string | null;
  completedAt: string | null;
};

type AccountResp = {
  company: string;
  viewers: Array<{
    issuanceId: string;
    recipientName: string;
    viewerId: string;
    viewerName: string | null;
    viewerEmail: string | null;
    viewerCompany: string | null;
  }>;
};

// Persists the date range in sessionStorage per user (spec §6.5).
const RANGE_KEY = "demore-analytics-range";
type RangePreset = "month" | "quarter" | "year" | "all";

function loadRange(): RangePreset {
  try {
    return (sessionStorage.getItem(RANGE_KEY) as RangePreset) || "month";
  } catch {
    return "month";
  }
}
function saveRange(r: RangePreset) {
  try { sessionStorage.setItem(RANGE_KEY, r); } catch { /* */ }
}

function rangeBounds(r: RangePreset): { from: Date | null; to: Date | null } {
  const now = new Date();
  if (r === "all") return { from: null, to: null };
  if (r === "month") return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: null };
  if (r === "quarter") {
    const q = Math.floor(now.getMonth() / 3) * 3;
    return { from: new Date(now.getFullYear(), q, 1), to: null };
  }
  return { from: new Date(now.getFullYear(), 0, 1), to: null };
}

export function AnalyticsPage() {
  const [range, setRange] = useState<RangePreset>(loadRange());
  const [senderId, setSenderId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [category, setCategory] = useState("");
  const { from, to } = useMemo(() => rangeBounds(range), [range]);

  const { data: users } = useQuery({
    queryKey: ["users-for-analytics"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; role: string }> }>("/api/admin/users"),
  });
  const { data: campaigns } = useQuery({
    queryKey: ["campaigns-for-analytics"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>("/api/admin/campaigns"),
  });

  const params = new URLSearchParams();
  if (from) params.set("from", from.toISOString());
  if (to) params.set("to", to.toISOString());
  if (senderId) params.set("senderId", senderId);
  if (campaignId) params.set("campaignId", campaignId);
  if (category) params.set("category", category);

  const { data } = useQuery({
    queryKey: ["analytics-portfolio", range, senderId, campaignId, category],
    queryFn: () => api.get<{ items: Item[] }>(`/api/admin/analytics/portfolio?${params}`),
  });
  const items = data?.items ?? [];
  const totals = {
    sent: items.length,
    opened: items.filter((i) => i.firstOpenedAt).length,
    completed: items.filter((i) => i.completedAt).length,
  };

  const [companyQuery, setCompanyQuery] = useState("");
  const [searched, setSearched] = useState<string | null>(null);
  const { data: account } = useQuery({
    queryKey: ["analytics-account", searched],
    queryFn: () => api.get<AccountResp>(`/api/admin/analytics/account?company=${encodeURIComponent(searched ?? "")}`),
    enabled: !!searched,
  });

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-head text-2xl font-semibold">Analytics</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={range}
            onChange={(e) => { const r = e.target.value as RangePreset; setRange(r); saveRange(r); }}
            className="w-36"
            title="Date range"
          >
            <option value="month">This month</option>
            <option value="quarter">This quarter</option>
            <option value="year">This year</option>
            <option value="all">All time</option>
          </Select>
          <Select value={senderId} onChange={(e) => setSenderId(e.target.value)} className="w-44" title="AE / sender">
            <option value="">Any sender</option>
            {users?.items.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
          <Select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="w-44" title="Campaign">
            <option value="">Any campaign</option>
            {campaigns?.items.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={category} onChange={(e) => setCategory(e.target.value)} className="w-44" title="Video category">
            <option value="">Any category</option>
            {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardHeader><CardTitle>Sent</CardTitle></CardHeader><CardContent><div className="text-3xl font-semibold text-gold">{totals.sent}</div></CardContent></Card>
        <Card><CardHeader><CardTitle>Opened</CardTitle></CardHeader><CardContent><div className="text-3xl font-semibold text-gold">{totals.opened}</div></CardContent></Card>
        <Card><CardHeader><CardTitle>Completed</CardTitle></CardHeader><CardContent><div className="text-3xl font-semibold text-gold">{totals.completed}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Per-account buying committee</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Company name (exact, case-sensitive)"
              value={companyQuery}
              onChange={(e) => setCompanyQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setSearched(companyQuery.trim()); }}
            />
            <Button onClick={() => setSearched(companyQuery.trim())}>Search</Button>
          </div>
          {account && (
            <div className="text-sm">
              <div className="text-muted-foreground mb-2">
                {account.viewers.length} viewer(s) at <strong>{account.company}</strong>
              </div>
              <ul className="space-y-1">
                {account.viewers.map((v) => (
                  <li key={v.viewerId} className="flex items-center justify-between border-b border-admin-rule py-1">
                    <Link to={`/admin/analytics/viewer/${v.viewerId}`} className="hover:text-gold">
                      {v.viewerName ?? v.viewerEmail ?? "(anon)"}
                    </Link>
                    <Link to={`/admin/issuances/${v.issuanceId}`} className="text-xs text-muted-foreground hover:text-gold">
                      pack: {v.recipientName}
                    </Link>
                  </li>
                ))}
                {account.viewers.length === 0 && <li className="text-xs text-muted-foreground">No viewers found.</li>}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Recent activity</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-admin-rule text-left">
              <th className="p-2">Recipient</th><th className="p-2">Company</th><th className="p-2">Sent</th><th className="p-2">Status</th>
            </tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b border-admin-rule">
                  <td className="p-2">
                    <Link to={`/admin/issuances/${i.id}`} className="hover:text-gold">{i.recipientName}</Link>
                  </td>
                  <td className="p-2">{i.recipientCompany ?? "—"}</td>
                  <td className="p-2 text-xs">{formatDateTime(i.createdAt)}</td>
                  <td className="p-2 text-xs">{i.completedAt ? "Completed" : i.firstOpenedAt ? "Opened" : "Unopened"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
